import { isOfferAvailableNow } from "@/lib/is-offer-available-now";
import type { Availability, Category, Item, ItemOffer, OptionGroup, Price } from "@/types";

import type { Suggestion } from "./types";

/** Slice of the live catalog the join needs — same shape every surface already has in atoms. */
export type SuggestionCatalog = {
  itemOffers: ItemOffer[];
  items: Item[];
  optionGroups: OptionGroup[];
  availabilities: Availability[];
  /** Owning visible category per offer id; when given, offers outside it are off the shelf and never suggested. */
  categoryByOfferId?: Map<string, Pick<Category, "status" | "availabilityId">>;
};

/** One suggestion, joined with the live catalog and ready to render. */
export type RenderableSuggestion = {
  merchantItemId: string;
  itemOfferId: string;
  name: string;
  price: Price;
  imageUrl?: string;
  reason: Suggestion["reason"];
  arm: Suggestion["arm"];
  anchorItemId?: string;
  /** No required option group: the "+" adds straight to the bag. Otherwise it must open item detail. */
  oneTapAddable: boolean;
  itemOffer: ItemOffer;
  item: Item;
};

/** True when at least one of the offer's option groups is required (`minPermitted > 0`). */
export function offerHasRequiredOptions(itemOffer: Pick<ItemOffer, "optionGroupsId">, optionGroups: OptionGroup[]): boolean {
  if (!itemOffer.optionGroupsId || itemOffer.optionGroupsId.length === 0) return false;
  const ids = new Set(itemOffer.optionGroupsId);
  return optionGroups.some((group) => ids.has(group.id) && group.minPermitted > 0);
}

/**
 * Joins ranked suggestion ids with the live, channel-scoped catalog.
 *
 * Backend order is preserved (it is already ranked); this step only DROPS —
 * offer not found or off the visible shelf, item or offer not orderable right
 * now, or already in the bag — and caps
 * the result. Never reorders, never re-ranks.
 *
 * Arm-aware per the exploit/explore policy: `exploreSlots` of the `cap`
 * positions are reserved for `"EXPLORE"` candidates, the rest for
 * `"EXPLOIT"`. Walking the (already ranked) list, a candidate whose arm quota
 * is already full is set aside instead of dropped; if the walk still leaves
 * fewer than `cap` accepted, those set-aside candidates backfill the
 * remainder in server order, any arm. Never re-ranks.
 */
export function joinSuggestionsToCatalog(
  suggestions: Suggestion[],
  catalog: SuggestionCatalog,
  cartItemIds: ReadonlySet<string>,
  cap: number,
  exploreSlots = 0,
): RenderableSuggestion[] {
  const shelf = catalog.categoryByOfferId;
  const offerByItemId = new Map<string, ItemOffer>();
  for (const offer of catalog.itemOffers) {
    if (shelf && !shelf.has(offer.id)) continue;
    if (!offerByItemId.has(offer.itemId)) offerByItemId.set(offer.itemId, offer);
  }
  const itemById = new Map(catalog.items.map((item) => [item.id, item]));

  const exploreQuota = Math.max(0, Math.min(exploreSlots, cap));
  const exploitQuota = cap - exploreQuota;

  const accepted: RenderableSuggestion[] = [];
  const quotaSkipped: RenderableSuggestion[] = [];
  const seen = new Set<string>();
  let exploreAccepted = 0;
  let exploitAccepted = 0;

  for (const suggestion of suggestions) {
    if (accepted.length >= cap) break;
    if (seen.has(suggestion.merchantItemId)) continue;
    if (cartItemIds.has(suggestion.merchantItemId)) continue;

    const itemOffer = offerByItemId.get(suggestion.merchantItemId);
    if (!itemOffer) continue;

    const item = itemById.get(itemOffer.itemId);
    if (!item || item.status === "UNAVAILABLE") continue;

    const category = shelf?.get(itemOffer.id) ?? null;
    if (!isOfferAvailableNow(itemOffer, catalog.availabilities, category)) continue;

    seen.add(suggestion.merchantItemId);
    const entry: RenderableSuggestion = {
      merchantItemId: suggestion.merchantItemId,
      itemOfferId: itemOffer.id,
      name: item.name || suggestion.name,
      price: itemOffer.price,
      imageUrl: item.image?.URL,
      reason: suggestion.reason,
      arm: suggestion.arm,
      ...(suggestion.anchorItemId ? { anchorItemId: suggestion.anchorItemId } : {}),
      oneTapAddable: !offerHasRequiredOptions(itemOffer, catalog.optionGroups),
      itemOffer,
      item,
    };

    const quotaFull = suggestion.arm === "EXPLORE" ? exploreAccepted >= exploreQuota : exploitAccepted >= exploitQuota;
    if (quotaFull) {
      quotaSkipped.push(entry);
      continue;
    }

    if (suggestion.arm === "EXPLORE") exploreAccepted++;
    else exploitAccepted++;
    accepted.push(entry);
  }

  for (const entry of quotaSkipped) {
    if (accepted.length >= cap) break;
    accepted.push(entry);
  }

  return accepted;
}
